import { useEffect, useMemo, useState } from 'react';
import Modal from 'react-bootstrap/Modal';
import Button from 'react-bootstrap/Button';
import { toast } from 'react-toastify';

import { AuthService, type ImpersonableUser } from 'api/services/authService';
import { useAuthStore } from 'store/authStore';
import { useMenuStore } from 'store/menuStore';
import { homePathFor } from 'routes/homeByProfile';

// ============================================================================
// Emular usuario — solo superadmin
// ============================================================================
//
// Para qué: comprobar qué ve cada perfil sin pedirle su contraseña ni cerrar la
// propia sesión. Se descartó la otra idea —copiar los items de menú de cada
// perfil bajo un item del superadmin— porque eso solo habría mostrado los
// NOMBRES de las pantallas, nunca los datos que cada quien alcanza, y se
// desincronizaría en el primer cambio de permisos.
//
// Agrupado por perfil y no una lista plana: lo que se quiere revisar casi
// siempre es "un auditor", no una persona en particular, y así se ve de un
// golpe qué perfiles tienen usuarios con los que probar.
// ============================================================================

export default function ImpersonateModal({ show, onHide }: { show: boolean; onHide: () => void }) {
  const setImpersonation = useAuthStore((s) => s.setImpersonation);
  const clearMenuItems = useMenuStore((s) => s.clearItems);

  const [usuarios, setUsuarios] = useState<ImpersonableUser[] | null>(null);
  const [busca, setBusca] = useState('');
  const [entrando, setEntrando] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Se piden al ABRIR y no al montar: el modal vive en el encabezado de todas
  // las pantallas, y una lista de usuarios en cada carga de página sería una
  // consulta que casi nunca se usa.
  useEffect(() => {
    if (!show) return;
    setBusca('');
    setError(null);
    AuthService.impersonationUsers()
      .then(setUsuarios)
      .catch(() => setError('No se pudo cargar la lista de usuarios.'));
  }, [show]);

  const filtrados = useMemo(() => {
    const q = busca.trim().toLowerCase();
    const base = usuarios ?? [];
    if (q === '') return base;
    return base.filter((u) =>
      [u.name, u.email, u.profileName, u.position].some((c) => (c ?? '').toLowerCase().includes(q))
    );
  }, [usuarios, busca]);

  // Agrupado conservando el orden que ya trae el servidor (por perfil, luego
  // por nombre): un Map respeta el orden de inserción, un objeto no lo
  // garantiza para llaves de texto.
  const porPerfil = useMemo(() => {
    const m = new Map<string, ImpersonableUser[]>();
    for (const u of filtrados) {
      const k = u.profileName ?? 'Sin perfil';
      if (!m.has(k)) m.set(k, []);
      m.get(k)!.push(u);
    }
    return [...m.entries()];
  }, [filtrados]);

  const emular = async (u: ImpersonableUser) => {
    setEntrando(u.keyUser);
    setError(null);
    try {
      const s = await AuthService.impersonate(u.keyUser);
      setImpersonation(s.token, s.user, s.impersonatedBy);
      clearMenuItems();

      // Recarga COMPLETA del navegador, no navigate(): media aplicación tiene
      // estado ya cargado con los permisos del superadmin —el menú, los
      // catálogos, las tablas, el WebSocket— y se quedaría pintado encima del
      // usuario emulado. Montar todo de cero es la única forma de ver de
      // verdad lo que ese usuario ve.
      window.location.href = homePathFor(s.user.keyProfile);
    } catch (e: any) {
      setEntrando(null);
      setError(e?.response?.data?.metadata?.msg ?? 'No se pudo emular a este usuario.');
      toast.error('No se pudo emular a este usuario.');
    }
  };

  return (
    <Modal show={show} onHide={onHide} centered size="lg" scrollable>
      <Modal.Header closeButton>
        <Modal.Title style={{ fontSize: '1.0625rem' }}>
          <i className="ph ph-user-switch me-2" />Ver el sistema como otro usuario
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <p className="text-muted mb-3" style={{ fontSize: '.875rem' }}>
          Se abre la sesión de ese usuario con sus permisos, su menú y sus obras.
          Para volver a la tuya, usa el botón de la franja que aparecerá arriba.
          {/* Se dice que SÍ se puede escribir: creer que es una vista de solo
              lectura y guardar algo "de prueba" en producción es el accidente
              que este aviso trata de evitar. */}
          {' '}Mientras emulas <strong>puedes modificar datos</strong>, y todo
          queda en la bitácora a nombre de los dos.
        </p>

        <div className="input-group input-group-sm mb-3">
          <span className="input-group-text"><i className="ph ph-magnifying-glass" /></span>
          <input
            className="form-control" autoFocus
            style={{ fontSize: '.9375rem' }}
            placeholder="Buscar por nombre, correo, perfil o puesto"
            value={busca} onChange={(e) => setBusca(e.target.value)}
          />
        </div>

        {error && <div className="alert alert-danger py-2" style={{ fontSize: '.875rem' }}>{error}</div>}

        {usuarios === null && !error && (
          <p className="text-muted mb-0" style={{ fontSize: '.875rem' }}>Cargando usuarios…</p>
        )}

        {usuarios !== null && porPerfil.length === 0 && (
          <p className="text-muted mb-0" style={{ fontSize: '.875rem' }}>
            Ningún usuario coincide con la búsqueda.
          </p>
        )}

        {porPerfil.map(([perfil, lista]) => (
          <div key={perfil} className="mb-3">
            <div className="d-flex align-items-center gap-2 mb-2">
              <span className="badge text-bg-primary" style={{ fontSize: '.8125rem' }}>{perfil}</span>
              <span className="text-muted" style={{ fontSize: '.8125rem' }}>
                {lista.length} usuario{lista.length === 1 ? '' : 's'}
              </span>
            </div>
            <div className="list-group">
              {lista.map((u) => (
                <button
                  key={u.keyUser} type="button"
                  className="list-group-item list-group-item-action d-flex justify-content-between align-items-center gap-2"
                  disabled={entrando !== null}
                  onClick={() => emular(u)}
                >
                  <span className="text-start">
                    <span className="d-block" style={{ fontSize: '.9375rem' }}>{u.name}</span>
                    <span className="d-block text-muted" style={{ fontSize: '.8125rem' }}>
                      {u.email}{u.position ? ` · ${u.position}` : ''}
                    </span>
                  </span>
                  <span className="text-nowrap" style={{ fontSize: '.8125rem' }}>
                    {entrando === u.keyUser
                      ? <><i className="ph ph-spinner me-1" />Entrando…</>
                      : <><i className="ph ph-arrow-right me-1" />Emular</>}
                  </span>
                </button>
              ))}
            </div>
          </div>
        ))}
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" size="sm" onClick={onHide} disabled={entrando !== null}>Cerrar</Button>
      </Modal.Footer>
    </Modal>
  );
}
